import { Element, Descendant } from "slate";
import {
  CustomElement,
  CustomText,
  BlockType,
} from "../../../admin/components/RichTextEditor/RichTextEditor.types";

export const markdownToSlate = (markdown: string): CustomElement[] => {
  if (!markdown) {
    return [{ type: "paragraph" as BlockType, children: [{ text: "" }] }];
  }

  // Split markdown into paragraphs
  const paragraphs = markdown.split(/\n{2,}/);
  const elements: CustomElement[] = [];

  for (const paragraph of paragraphs) {
    const trimmedParagraph = paragraph.trim();

    if (!trimmedParagraph) {
      elements.push({
        type: "paragraph" as BlockType,
        children: [{ text: "" }],
      });
      continue;
    }

    // Handle headings (# Heading)
    if (trimmedParagraph.startsWith("# ")) {
      elements.push({
        type: "heading-one" as BlockType,
        children: parseInlineFormatting(trimmedParagraph.substring(2)),
      });
    }
    // Handle subheadings (## Subheading)
    else if (trimmedParagraph.startsWith("## ")) {
      elements.push({
        type: "heading-two" as BlockType,
        children: parseInlineFormatting(trimmedParagraph.substring(3)),
      });
    }
    // Handle block quotes
    else if (trimmedParagraph.startsWith("> ")) {
      elements.push({
        type: "block-quote" as BlockType,
        children: parseInlineFormatting(trimmedParagraph.substring(2)),
      });
    }
    // Handle bulleted lists
    else if (trimmedParagraph.match(/^(- |\* )/m)) {
      const items = trimmedParagraph.split(/\n(- |\* )/);
      const listItems: CustomElement[] = [];

      for (let i = 0; i < items.length; i++) {
        // First item needs special handling because of split behavior
        if (i === 0) {
          const itemText = items[i].replace(/^(- |\* )/, "");
          if (itemText.trim()) {
            listItems.push({
              type: "list-item" as BlockType,
              children: parseInlineFormatting(itemText),
            });
          }
        } else if (i % 2 === 1) {
          // Skip separator matches
          continue;
        } else if (items[i].trim()) {
          listItems.push({
            type: "list-item" as BlockType,
            children: parseInlineFormatting(items[i]),
          });
        }
      }

      elements.push({
        type: "bulleted-list" as BlockType,
        children: listItems,
      });
    }
    // Handle numbered lists
    else if (trimmedParagraph.match(/^\d+\. /m)) {
      const items = trimmedParagraph.split(/\n\d+\. /);
      const listItems: CustomElement[] = [];

      for (let i = 0; i < items.length; i++) {
        // First item needs special handling
        if (i === 0) {
          const itemText = items[i].replace(/^\d+\. /, "");
          if (itemText.trim()) {
            listItems.push({
              type: "list-item" as BlockType,
              children: parseInlineFormatting(itemText),
            });
          }
        } else if (items[i].trim()) {
          listItems.push({
            type: "list-item" as BlockType,
            children: parseInlineFormatting(items[i]),
          });
        }
      }

      elements.push({
        type: "numbered-list" as BlockType,
        children: listItems,
      });
    }
    // Handle code blocks
    else if (trimmedParagraph.startsWith("```")) {
      const codeBlockParts = trimmedParagraph.split("\n");
      const language = codeBlockParts[0].replace("```", "").trim();
      const code = codeBlockParts.slice(1, -1).join("\n");

      elements.push({
        type: "code-block" as BlockType,
        //@ts-ignore
        language: language || "text",
        children: [{ text: code }],
      });
    }
    // Handle paragraphs (default)
    else {
      const lines = trimmedParagraph.split("\n");
      for (const line of lines) {
        if (line.trim()) {
          elements.push({
            type: "paragraph" as BlockType,
            children: parseInlineFormatting(line),
          });
        }
      }
    }
  }

  return elements.length > 0
    ? elements
    : [{ type: "paragraph" as BlockType, children: [{ text: "" }] }];
};

// Helper function to parse inline formatting and links
function parseInlineFormatting(text: string): CustomText[] {
  // Process links first
  const nodes: CustomText[] = [];
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link with its formatting
    if (match.index > lastIndex) {
      const textBeforeLink = text.substring(lastIndex, match.index);
      nodes.push(...parseTextWithFormatting(textBeforeLink));
    }

    // Add the link
    const linkText = match[1];
    const url = match[2];

    if (linkText.trim()) {
      nodes.push({
        text: linkText,
        link: url,
      } as CustomText);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after all links
  if (lastIndex < text.length || nodes.length === 0) {
    const remainingText =
      lastIndex < text.length ? text.substring(lastIndex) : text;
    nodes.push(...parseTextWithFormatting(remainingText));
  }

  return nodes.length > 0 ? nodes : [{ text: "" }];
}

// Helper function to parse text formatting (bold, italic, etc.)
function parseTextWithFormatting(text: string): CustomText[] {
  // For a more robust approach, we'll handle each formatting separately and in sequence

  // Start with an array containing the full text
  let segments: CustomText[] = [{ text }];

  // Helper to apply formatting to each segment
  const applyFormatting = (
    pattern: RegExp,
    formatKey: keyof CustomText,
    formatValue: boolean = true
  ) => {
    const newSegments: CustomText[] = [];

    segments.forEach((segment) => {
      // Skip segments that are already links or have other special handling
      if ("link" in segment) {
        newSegments.push(segment);
        return;
      }

      const parts = segment.text.split(pattern);

      if (parts.length <= 1) {
        // No match found, keep segment as is
        newSegments.push(segment);
        return;
      }

      for (let i = 0; i < parts.length; i++) {
        if (parts[i] === "") continue;

        if (i % 2 === 0) {
          // Regular text
          newSegments.push({ ...segment, text: parts[i] });
        } else {
          // Formatted text
          const formattedSegment = { ...segment, text: parts[i] };
          // @ts-ignore
          formattedSegment[formatKey] = formatValue as any;
          newSegments.push(formattedSegment);
        }
      }
    });

    segments = newSegments;
  };

  // Apply different formatting patterns in sequence
  applyFormatting(/\*\*(.*?)\*\*/g, "bold");
  applyFormatting(/\*(.*?)\*/g, "italic");
  applyFormatting(/__(.*?)__/g, "underline");
  applyFormatting(/~~(.*?)~~/g, "strikethrough");
  applyFormatting(/`(.*?)`/g, "code");

  return segments;
}
